import { useMemo, useRef, useState, useEffect, useCallback } from 'react'
import { Canvas, useThree, useFrame } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import * as THREE from 'three'

// ── Colormaps ────────────────────────────────────────────────────────

const COLORMAPS = {
  rainbow: (t) => {
    let r, g, b
    if (t < 0.25) { const s = t / 0.25; r = 0; g = s; b = 1 }
    else if (t < 0.5) { const s = (t - 0.25) / 0.25; r = 0; g = 1; b = 1 - s }
    else if (t < 0.75) { const s = (t - 0.5) / 0.25; r = s; g = 1; b = 0 }
    else { const s = (t - 0.75) / 0.25; r = 1; g = 1 - s; b = 0 }
    return [r, g, b]
  },
  jet: (t) => {
    const r = Math.min(1, Math.max(0, 1.5 - Math.abs(t - 0.75) * 4))
    const g = Math.min(1, Math.max(0, 1.5 - Math.abs(t - 0.5) * 4))
    const b = Math.min(1, Math.max(0, 1.5 - Math.abs(t - 0.25) * 4))
    return [r, g, b]
  },
  coolwarm: (t) => {
    const r = Math.min(1, 0.23 + t * 0.77)
    const g = Math.min(1, Math.max(0, 0.3 + (0.5 - Math.abs(t - 0.5)) * 1.4))
    const b = Math.min(1, 1.0 - t * 0.77)
    return [r, g, b]
  },
  grayscale: (t) => [t, t, t],
}

const COLORMAP_LABELS = { rainbow: 'Rainbow', jet: 'Jet', coolwarm: 'Cool-Warm', grayscale: 'Grayscale' }
const COLORMAP_GRADIENTS = {
  rainbow: 'linear-gradient(to right, #3b82f6, #06b6d4, #22c55e, #eab308, #ef4444)',
  jet: 'linear-gradient(to right, #0000cc, #0088ff, #00ff00, #ffff00, #ff0000)',
  coolwarm: 'linear-gradient(to right, #3b4cc0, #8db0fe, #f7f7f7, #f4a582, #b40426)',
  grayscale: 'linear-gradient(to right, #000, #fff)',
}

function stressToColor(value, min, max, colormap = 'rainbow') {
  if (max === min) return [0.5, 0.5, 0.5]
  const t = Math.max(0, Math.min(1, (value - min) / (max - min)))
  return COLORMAPS[colormap](t)
}

// ── STL Parsing ──────────────────────────────────────────────────────

function parseSTL(buffer) {
  const view = new DataView(buffer)
  const header = new Uint8Array(buffer, 0, Math.min(80, buffer.byteLength))
  const headerStr = new TextDecoder().decode(header).trim()
  if (headerStr.startsWith('solid') && buffer.byteLength > 84) {
    const numTrisBinary = view.getUint32(80, true)
    const expectedSize = 84 + numTrisBinary * 50
    if (Math.abs(expectedSize - buffer.byteLength) > 10) return parseASCIISTL(buffer)
  }
  const numTriangles = view.getUint32(80, true)
  const positions = new Float32Array(numTriangles * 9)
  const normals = new Float32Array(numTriangles * 9)
  for (let i = 0; i < numTriangles; i++) {
    const offset = 84 + i * 50
    const nx = view.getFloat32(offset, true), ny = view.getFloat32(offset + 4, true), nz = view.getFloat32(offset + 8, true)
    for (let v = 0; v < 3; v++) {
      const vO = offset + 12 + v * 12
      positions[i * 9 + v * 3] = view.getFloat32(vO, true)
      positions[i * 9 + v * 3 + 1] = view.getFloat32(vO + 4, true)
      positions[i * 9 + v * 3 + 2] = view.getFloat32(vO + 8, true)
      normals[i * 9 + v * 3] = nx; normals[i * 9 + v * 3 + 1] = ny; normals[i * 9 + v * 3 + 2] = nz
    }
  }
  const geom = new THREE.BufferGeometry()
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3))
  geom.setAttribute('normal', new THREE.BufferAttribute(normals, 3))
  geom.computeBoundingSphere()
  return geom
}

function parseASCIISTL(buffer) {
  const text = new TextDecoder().decode(buffer)
  const verts = []
  const regex = /vertex\s+([\d.eE+-]+)\s+([\d.eE+-]+)\s+([\d.eE+-]+)/g
  let match
  while ((match = regex.exec(text)) !== null) verts.push(parseFloat(match[1]), parseFloat(match[2]), parseFloat(match[3]))
  const geom = new THREE.BufferGeometry()
  geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(verts), 3))
  geom.computeVertexNormals()
  geom.computeBoundingSphere()
  return geom
}

// ── 3D Components ────────────────────────────────────────────────────

function STLPreviewMesh({ geometry, wireframe, clippingPlane }) {
  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial
        color="#8899aa" roughness={0.5} metalness={0.3}
        side={THREE.DoubleSide} wireframe={wireframe}
        clippingPlanes={clippingPlane ? [clippingPlane] : []}
      />
    </mesh>
  )
}

function StressMesh({ nodeData, deformationScale, colormap, stressRange, clippingPlane }) {
  const geometry = useMemo(() => {
    if (!nodeData?.positions || !nodeData?.von_mises) return null
    const { positions, von_mises: stresses, displacements } = nodeData
    if (positions.length === 0) return null

    let minS = Infinity, maxS = -Infinity
    for (const s of stresses) { if (s > 0) { if (s < minS) minS = s; if (s > maxS) maxS = s } }
    if (minS === Infinity) minS = 0
    if (maxS === -Infinity) maxS = 1

    // Apply stress range filter
    const filterMin = stressRange ? stressRange[0] * maxS : 0
    const filterMax = stressRange ? stressRange[1] * maxS : maxS

    const filteredPos = []
    const filteredCol = []

    for (let i = 0; i < positions.length; i++) {
      const stress = stresses[i]
      if (stress < filterMin || stress > filterMax) continue

      const [x, y, z] = positions[i]
      const [dx, dy, dz] = displacements[i] || [0, 0, 0]
      filteredPos.push(x + dx * deformationScale, y + dy * deformationScale, z + dz * deformationScale)
      const [r, g, b] = stressToColor(stress, minS, maxS, colormap)
      filteredCol.push(r, g, b)
    }

    const geom = new THREE.BufferGeometry()
    geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(filteredPos), 3))
    geom.setAttribute('color', new THREE.BufferAttribute(new Float32Array(filteredCol), 3))
    geom.computeBoundingSphere()
    return geom
  }, [nodeData, deformationScale, colormap, stressRange])

  if (!geometry) return null
  return (
    <points geometry={geometry}>
      <pointsMaterial
        vertexColors size={1.5} sizeAttenuation transparent opacity={0.9}
        clippingPlanes={clippingPlane ? [clippingPlane] : []}
      />
    </points>
  )
}

// Clipping plane helper — visual plane in the scene
function ClipPlaneHelper({ clippingPlane, size }) {
  const meshRef = useRef()
  useFrame(() => {
    if (!meshRef.current || !clippingPlane) return
    const normal = clippingPlane.normal
    const dist = clippingPlane.constant
    meshRef.current.position.copy(normal.clone().multiplyScalar(-dist))
    meshRef.current.lookAt(meshRef.current.position.clone().add(normal))
  })

  if (!clippingPlane) return null
  return (
    <mesh ref={meshRef}>
      <planeGeometry args={[size, size]} />
      <meshBasicMaterial color="#3b82f6" transparent opacity={0.08} side={THREE.DoubleSide} />
    </mesh>
  )
}

// Enable clipping on the renderer
function ClipEnabler({ enabled }) {
  const { gl } = useThree()
  useEffect(() => {
    gl.localClippingEnabled = enabled
  }, [gl, enabled])
  return null
}

// Hover info — raycasts on pointer move to find nearest node
function HoverProbe({ nodeData, onHover }) {
  const { camera, raycaster, pointer } = useThree()
  const pointsRef = useRef()

  // Build a points geometry for raycasting
  const geometry = useMemo(() => {
    if (!nodeData?.positions) return null
    const n = nodeData.positions.length
    const arr = new Float32Array(n * 3)
    for (let i = 0; i < n; i++) {
      arr[i * 3] = nodeData.positions[i][0]
      arr[i * 3 + 1] = nodeData.positions[i][1]
      arr[i * 3 + 2] = nodeData.positions[i][2]
    }
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.BufferAttribute(arr, 3))
    return g
  }, [nodeData])

  useFrame(() => {
    if (!geometry || !pointsRef.current) return
    raycaster.setFromCamera(pointer, camera)
    raycaster.params.Points.threshold = 1.5
    const intersects = raycaster.intersectObject(pointsRef.current)
    if (intersects.length > 0) {
      const idx = intersects[0].index
      onHover({
        index: idx,
        position: nodeData.positions[idx],
        stress: nodeData.von_mises[idx],
        displacement: nodeData.displacements[idx],
      })
    } else {
      onHover(null)
    }
  })

  if (!geometry) return null
  return (
    <points ref={pointsRef} geometry={geometry} visible={false}>
      <pointsMaterial size={3} />
    </points>
  )
}

// Measurement tool — click two points to measure distance
function MeasureTool({ nodeData, active, onMeasure }) {
  const { camera, raycaster, pointer, scene } = useThree()
  const pointsRef = useRef()
  const [point1, setPoint1] = useState(null)

  const geometry = useMemo(() => {
    if (!nodeData?.positions || !active) return null
    const n = nodeData.positions.length
    const arr = new Float32Array(n * 3)
    for (let i = 0; i < n; i++) {
      arr[i * 3] = nodeData.positions[i][0]
      arr[i * 3 + 1] = nodeData.positions[i][1]
      arr[i * 3 + 2] = nodeData.positions[i][2]
    }
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.BufferAttribute(arr, 3))
    return g
  }, [nodeData, active])

  useEffect(() => {
    if (!active) { setPoint1(null); onMeasure(null) }
  }, [active, onMeasure])

  useEffect(() => {
    if (!active || !geometry || !pointsRef.current) return
    const handleClick = (e) => {
      const rect = e.target.getBoundingClientRect()
      const mouse = new THREE.Vector2(
        ((e.clientX - rect.left) / rect.width) * 2 - 1,
        -((e.clientY - rect.top) / rect.height) * 2 + 1
      )
      raycaster.setFromCamera(mouse, camera)
      raycaster.params.Points.threshold = 2.0
      const hits = raycaster.intersectObject(pointsRef.current)
      if (hits.length === 0) return

      const pos = [hits[0].point.x, hits[0].point.y, hits[0].point.z]
      if (!point1) {
        setPoint1(pos)
        onMeasure({ point1: pos, point2: null, distance: null })
      } else {
        const d = Math.sqrt((pos[0]-point1[0])**2 + (pos[1]-point1[1])**2 + (pos[2]-point1[2])**2)
        onMeasure({ point1, point2: pos, distance: d })
        setPoint1(null)
      }
    }
    const canvas = document.querySelector('canvas')
    if (canvas) canvas.addEventListener('click', handleClick)
    return () => { if (canvas) canvas.removeEventListener('click', handleClick) }
  }, [active, geometry, point1, camera, raycaster, onMeasure])

  if (!geometry || !active) return null
  return (
    <points ref={pointsRef} geometry={geometry} visible={false}>
      <pointsMaterial size={3} />
    </points>
  )
}

// Measurement line visual
function MeasureLine({ measurement }) {
  if (!measurement?.point1) return null
  const p1 = measurement.point1
  const p2 = measurement.point2 || p1

  const lineGeom = useMemo(() => {
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([...p1, ...p2]), 3))
    return g
  }, [p1, p2])

  return (
    <group>
      {/* Point markers */}
      <mesh position={p1}>
        <sphereGeometry args={[0.5, 8, 8]} />
        <meshBasicMaterial color="#f59e0b" />
      </mesh>
      {measurement.point2 && (
        <>
          <mesh position={p2}>
            <sphereGeometry args={[0.5, 8, 8]} />
            <meshBasicMaterial color="#f59e0b" />
          </mesh>
          <line geometry={lineGeom}>
            <lineBasicMaterial color="#f59e0b" linewidth={2} />
          </line>
        </>
      )}
    </group>
  )
}

// Deformation animation — interpolates scale from 0 to target
function AnimatedStressMesh({ nodeData, targetScale, colormap, stressRange, clippingPlane, playing }) {
  const [animScale, setAnimScale] = useState(0)
  const direction = useRef(1)

  useFrame((_, delta) => {
    if (!playing) return
    setAnimScale(prev => {
      let next = prev + direction.current * delta * 20 // speed: 20 units/sec
      if (next >= targetScale) { next = targetScale; direction.current = -1 }
      if (next <= 0) { next = 0; direction.current = 1 }
      return next
    })
  })

  return <StressMesh nodeData={nodeData} deformationScale={playing ? animScale : targetScale}
    colormap={colormap} stressRange={stressRange} clippingPlane={clippingPlane} />
}

function AutoFitCamera({ geometry, controlsRef }) {
  useEffect(() => {
    if (!geometry?.boundingSphere || !controlsRef.current) return
    const { center, radius } = geometry.boundingSphere
    const camera = controlsRef.current.object
    camera.position.set(center.x + radius * 2, center.y + radius, center.z + radius * 2)
    controlsRef.current.target.copy(center)
    controlsRef.current.update()
  }, [geometry, controlsRef])

  return <OrbitControls ref={controlsRef} enableDamping dampingFactor={0.05} />
}

// Screenshot helper
function ScreenshotButton({ canvasRef }) {
  const handleScreenshot = useCallback(() => {
    const canvas = canvasRef.current?.querySelector('canvas')
    if (!canvas) return
    const w = canvas.width, h = canvas.height
    const offscreen = document.createElement('canvas')
    offscreen.width = w; offscreen.height = h
    const ctx = offscreen.getContext('2d')
    ctx.drawImage(canvas, 0, 0)
    ctx.save(); ctx.globalAlpha = 0.15
    ctx.font = `bold ${Math.max(24, w * 0.04)}px -apple-system, sans-serif`
    ctx.fillStyle = '#ffffff'; ctx.textAlign = 'center'
    ctx.fillText('Osteo3d FEA', w / 2, h / 2)
    ctx.globalAlpha = 0.3
    ctx.font = `${Math.max(12, w * 0.015)}px -apple-system, sans-serif`
    ctx.fillText('osteo3d.com', w / 2, h / 2 + Math.max(20, w * 0.03))
    ctx.restore()
    ctx.save(); ctx.globalAlpha = 0.6
    ctx.font = `${Math.max(10, w * 0.012)}px -apple-system, sans-serif`
    ctx.fillStyle = '#888'; ctx.textAlign = 'right'
    ctx.fillText('Osteo3d FEA — osteo3d.com', w - 12, h - 10)
    ctx.restore()
    const link = document.createElement('a')
    link.download = `osteo3d-fea-${Date.now()}.png`
    link.href = offscreen.toDataURL('image/png')
    link.click()
  }, [canvasRef])

  return (
    <ToolBtn title="Screenshot" onClick={handleScreenshot}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
    </ToolBtn>
  )
}

function computeSTLStats(geometry) {
  if (!geometry) return null
  const pos = geometry.getAttribute('position')
  const triCount = pos ? Math.floor(pos.count / 3) : 0
  geometry.computeBoundingBox()
  const bb = geometry.boundingBox
  return { triCount, sizeX: (bb.max.x - bb.min.x).toFixed(2), sizeY: (bb.max.y - bb.min.y).toFixed(2), sizeZ: (bb.max.z - bb.min.z).toFixed(2) }
}

function getVerdictInfo(sf) {
  if (sf >= 3) return { label: 'Safe', color: 'text-green-400', bgGlow: 'shadow-green-500/10' }
  if (sf >= 1.5) return { label: 'Adequate', color: 'text-blue-400', bgGlow: 'shadow-blue-500/10' }
  if (sf >= 1) return { label: 'Marginal', color: 'text-yellow-400', bgGlow: 'shadow-yellow-500/10' }
  return { label: 'Fail', color: 'text-red-400', bgGlow: 'shadow-red-500/10' }
}

function stressPlainText(mpa) {
  if (mpa < 1) return 'Very low'; if (mpa < 50) return 'Moderate'; if (mpa < 200) return 'Significant'; if (mpa < 500) return 'High'; return 'Extreme'
}

function dispPlainText(mm) {
  if (mm < 0.01) return 'No visible'; if (mm < 0.1) return 'Microscopic'; if (mm < 1) return 'Slight'; if (mm < 5) return 'Noticeable'; return 'Major'
}

function ToolBtn({ title, onClick, active, children }) {
  return (
    <button onClick={onClick} title={title}
      className={`p-2 rounded-lg transition-colors ${active ? 'bg-blue-600/30 text-blue-400' : 'bg-gray-800/80 text-gray-400 hover:text-white hover:bg-gray-700'}`}>
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>{children}</svg>
    </button>
  )
}

function setViewPreset(controlsRef, geometry, preset) {
  if (!geometry?.boundingSphere || !controlsRef.current) return
  const { center, radius } = geometry.boundingSphere
  const d = radius * 2.5
  const positions = {
    front: [center.x, center.y, center.z + d], back: [center.x, center.y, center.z - d],
    left: [center.x - d, center.y, center.z], right: [center.x + d, center.y, center.z],
    top: [center.x, center.y + d, center.z], bottom: [center.x, center.y - d, center.z],
    iso: [center.x + d * 0.7, center.y + d * 0.5, center.z + d * 0.7],
  }
  const cam = controlsRef.current.object
  cam.position.set(...(positions[preset] || positions.iso))
  controlsRef.current.target.copy(center)
  controlsRef.current.update()
}

// ── Main Component ───────────────────────────────────────────────────

export default function StressViewer({ stlFile, nodeData, results }) {
  const canvasWrapRef = useRef()
  const controlsRef = useRef()
  const [deformationScale, setDeformationScale] = useState(1)
  const [stlGeometry, setStlGeometry] = useState(null)
  const [stlStats, setStlStats] = useState(null)
  const [wireframe, setWireframe] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [colormap, setColormap] = useState('rainbow')
  const [showViewMenu, setShowViewMenu] = useState(false)
  const [clipEnabled, setClipEnabled] = useState(false)
  const [clipAxis, setClipAxis] = useState('x')
  const [clipPosition, setClipPosition] = useState(50) // 0-100%
  const [stressRange, setStressRange] = useState([0, 1]) // 0-1 normalized
  const [showStressFilter, setShowStressFilter] = useState(false)
  const [hoveredNode, setHoveredNode] = useState(null)
  const [measureActive, setMeasureActive] = useState(false)
  const [measurement, setMeasurement] = useState(null)
  const [animPlaying, setAnimPlaying] = useState(false)
  const hasResults = nodeData?.positions?.length > 0

  // Parse STL
  useEffect(() => {
    if (!stlFile) { setStlGeometry(null); setStlStats(null); return }
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const geom = parseSTL(e.target.result)
        setStlGeometry(geom)
        setStlStats(computeSTLStats(geom))
      } catch { setStlGeometry(null); setStlStats(null) }
    }
    reader.readAsArrayBuffer(stlFile)
  }, [stlFile])

  // Fullscreen
  const toggleFullscreen = useCallback(() => {
    const el = canvasWrapRef.current
    if (!el) return
    if (!document.fullscreenElement) el.requestFullscreen().catch(() => {})
    else document.exitFullscreen().catch(() => {})
  }, [])

  useEffect(() => {
    const handler = () => {
      setTimeout(() => {
        setIsFullscreen(!!document.fullscreenElement)
        // Force canvas resize after fullscreen transition
        const canvas = canvasWrapRef.current?.querySelector('canvas')
        if (canvas) {
          canvas.style.width = '100%'
          canvas.style.height = '100%'
          window.dispatchEvent(new Event('resize'))
        }
      }, 200)
    }
    document.addEventListener('fullscreenchange', handler)
    return () => document.removeEventListener('fullscreenchange', handler)
  }, [])

  const resetView = useCallback(() => setViewPreset(controlsRef, stlGeometry, 'iso'), [stlGeometry])

  // Compute clipping plane from axis + position
  const clippingPlane = useMemo(() => {
    if (!clipEnabled || !stlGeometry?.boundingBox) return null
    const bb = stlGeometry.boundingBox
    const axisIdx = { x: 0, y: 1, z: 2 }[clipAxis]
    const min = [bb.min.x, bb.min.y, bb.min.z][axisIdx]
    const max = [bb.max.x, bb.max.y, bb.max.z][axisIdx]
    const pos = min + (max - min) * (clipPosition / 100)
    const normal = new THREE.Vector3(0, 0, 0)
    normal.setComponent(axisIdx, -1)
    return new THREE.Plane(normal, pos)
  }, [clipEnabled, clipAxis, clipPosition, stlGeometry])

  const showViewer = stlGeometry || hasResults
  const sf = results?.results?.safety_factor
  const verdict = sf != null ? getVerdictInfo(sf) : null
  const maxMpa = results?.results?.max_von_mises_mpa
  const maxDisp = results?.results?.max_displacement_mm
  const bboxSize = stlGeometry?.boundingSphere?.radius * 3 || 200

  if (!showViewer) {
    return (
      <div className="w-full h-full relative bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <svg className="w-20 h-20 mx-auto text-gray-800 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          <p className="text-gray-600 text-sm">Upload an STL file to preview</p>
          <p className="text-gray-700 text-xs mt-1">Drag &amp; drop or click to browse</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full h-full relative bg-gray-950 overflow-hidden" ref={canvasWrapRef}>
      <Canvas
        camera={{ position: [0, 0, 200], fov: 45, near: 0.01, far: 100000 }}
        gl={{ antialias: true, alpha: false, preserveDrawingBuffer: true }}
      >
        <ClipEnabler enabled={clipEnabled} />
        <color attach="background" args={['#0a0a0a']} />
        <ambientLight intensity={0.5} />
        <directionalLight position={[10, 10, 10]} intensity={0.8} />
        <directionalLight position={[-10, -5, -10]} intensity={0.3} />

        {hasResults ? (
          <AnimatedStressMesh nodeData={nodeData} targetScale={deformationScale} colormap={colormap}
            stressRange={stressRange} clippingPlane={clippingPlane} playing={animPlaying} />
        ) : (
          stlGeometry && <STLPreviewMesh geometry={stlGeometry} wireframe={wireframe} clippingPlane={clippingPlane} />
        )}

        {clipEnabled && <ClipPlaneHelper clippingPlane={clippingPlane} size={bboxSize} />}
        {hasResults && !measureActive && <HoverProbe nodeData={nodeData} onHover={setHoveredNode} />}
        {hasResults && <MeasureTool nodeData={nodeData} active={measureActive} onMeasure={setMeasurement} />}
        {measurement && <MeasureLine measurement={measurement} />}

        <AutoFitCamera geometry={stlGeometry} controlsRef={controlsRef} />
        <gridHelper args={[500, 50, '#222', '#111']} />
      </Canvas>

      {/* Fullscreen header logo — top center to avoid overlap with results panel */}
      {isFullscreen && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 pointer-events-none" style={{ zIndex: 25 }}>
          <img src="/osteo3d-logo-white.png" alt="Osteo3d" className="h-6 w-auto opacity-60" />
        </div>
      )}

      {/* Background watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden" style={{ zIndex: 1 }}>
        <img src="/osteo3d-logo-white.png" alt="" draggable={false}
          style={{ width: '60%', maxWidth: '500px', opacity: 0.06, filter: 'brightness(2)' }} />
      </div>

      {/* ── Toolbar (right side) ────────────────────────────────────── */}
      <div className="absolute top-4 right-4 flex flex-col gap-1.5" style={{ zIndex: 20 }}>
        <ToolBtn title="Reset View" onClick={resetView}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
        </ToolBtn>

        <div className="relative">
          <ToolBtn title="View Presets" onClick={() => setShowViewMenu(!showViewMenu)}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16m-7 6h7" />
          </ToolBtn>
          {showViewMenu && (
            <div className="absolute right-10 top-0 bg-gray-900/95 border border-gray-700 rounded-lg p-1.5 backdrop-blur-md min-w-[100px]" style={{ zIndex: 30 }}>
              {['front', 'back', 'left', 'right', 'top', 'bottom', 'iso'].map(v => (
                <button key={v} onClick={() => { setViewPreset(controlsRef, stlGeometry, v); setShowViewMenu(false) }}
                  className="block w-full text-left px-3 py-1.5 text-[11px] text-gray-300 hover:bg-gray-700 rounded capitalize">
                  {v === 'iso' ? 'Isometric' : v}
                </button>
              ))}
            </div>
          )}
        </div>

        {!hasResults && (
          <ToolBtn title="Wireframe" onClick={() => setWireframe(!wireframe)} active={wireframe}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </ToolBtn>
        )}

        {/* Section / Clipping plane toggle */}
        <ToolBtn title="Section Plane" onClick={() => setClipEnabled(!clipEnabled)} active={clipEnabled}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </ToolBtn>

        {/* Stress filter toggle (results only) */}
        {hasResults && (
          <ToolBtn title="Stress Filter" onClick={() => setShowStressFilter(!showStressFilter)} active={showStressFilter}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </ToolBtn>
        )}

        {/* Measurement tool (results only) */}
        {hasResults && (
          <ToolBtn title={measureActive ? 'Cancel Measurement' : 'Measure Distance'} onClick={() => { setMeasureActive(!measureActive); if (measureActive) setMeasurement(null) }} active={measureActive}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662a4.006 4.006 0 00-3.7-3.7 48.678 48.678 0 00-7.324 0 4.006 4.006 0 00-3.7 3.7c-.017.22-.032.441-.046.662M19.5 12l3-3m-3 3l-3-3m-12 3c0 1.232.046 2.453.138 3.662a4.006 4.006 0 003.7 3.7 48.656 48.656 0 007.324 0 4.006 4.006 0 003.7-3.7c.017-.22.032-.441.046-.662M4.5 12l3 3m-3-3l-3 3" />
          </ToolBtn>
        )}

        {/* Deformation animation (results only) */}
        {hasResults && (
          <ToolBtn title={animPlaying ? 'Stop Animation' : 'Animate Deformation'} onClick={() => setAnimPlaying(!animPlaying)} active={animPlaying}>
            {animPlaying ? (
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25v13.5m-7.5-13.5v13.5" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
            )}
          </ToolBtn>
        )}

        <ToolBtn title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'} onClick={toggleFullscreen} active={isFullscreen}>
          {isFullscreen ? (
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
          )}
        </ToolBtn>

        <ScreenshotButton canvasRef={canvasWrapRef} />

        {hasResults && (
          <div className="mt-2 bg-gray-900/90 rounded-lg p-2 border border-gray-800 backdrop-blur-md">
            <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-1.5 text-center">Color Map</p>
            {Object.keys(COLORMAPS).map(cm => (
              <button key={cm} onClick={() => setColormap(cm)}
                className={`block w-full text-left px-2 py-1 rounded text-[10px] mb-0.5 transition-colors ${
                  colormap === cm ? 'bg-blue-600/30 text-blue-300' : 'text-gray-400 hover:bg-gray-700'
                }`}>{COLORMAP_LABELS[cm]}</button>
            ))}
          </div>
        )}
      </div>

      {/* ── Clipping plane controls (bottom right, above deformation) ── */}
      {clipEnabled && (
        <div className="absolute bottom-44 right-4 bg-gray-900/90 rounded-xl p-3 backdrop-blur-md border border-gray-800" style={{ zIndex: 20 }}>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Section Plane</p>
          <div className="flex gap-1 mb-2">
            {['x', 'y', 'z'].map(a => (
              <button key={a} onClick={() => setClipAxis(a)}
                className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase ${
                  clipAxis === a ? 'bg-blue-600/30 text-blue-300' : 'bg-gray-800 text-gray-500 hover:text-gray-300'
                }`}>{a}</button>
            ))}
          </div>
          <input type="range" min="0" max="100" step="1" value={clipPosition}
            onChange={e => setClipPosition(parseInt(e.target.value))} className="w-full accent-blue-500" />
          <p className="text-[9px] text-gray-600 text-center mt-1">Slide to move the cutting plane</p>
        </div>
      )}

      {/* ── Stress range filter (left side panel) ──────────────────── */}
      {showStressFilter && hasResults && (
        <div className="absolute bottom-20 left-4 bg-gray-900/90 rounded-xl p-4 backdrop-blur-md border border-gray-800" style={{ zIndex: 20 }}>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Stress Filter</p>
          <p className="text-[9px] text-gray-600 mb-2">Show nodes within stress range</p>
          <div className="space-y-2">
            <div>
              <div className="flex justify-between text-[9px] text-gray-500 mb-0.5">
                <span>Min: {(stressRange[0] * 100).toFixed(0)}%</span>
                <span>{(stressRange[0] * maxMpa).toFixed(1)} MPa</span>
              </div>
              <input type="range" min="0" max="100" step="1" value={stressRange[0] * 100}
                onChange={e => setStressRange([parseInt(e.target.value) / 100, stressRange[1]])}
                className="w-full accent-blue-500" />
            </div>
            <div>
              <div className="flex justify-between text-[9px] text-gray-500 mb-0.5">
                <span>Max: {(stressRange[1] * 100).toFixed(0)}%</span>
                <span>{(stressRange[1] * maxMpa).toFixed(1)} MPa</span>
              </div>
              <input type="range" min="0" max="100" step="1" value={stressRange[1] * 100}
                onChange={e => setStressRange([stressRange[0], parseInt(e.target.value) / 100])}
                className="w-full accent-red-500" />
            </div>
          </div>
          <button onClick={() => setStressRange([0, 1])}
            className="mt-2 text-[9px] text-blue-400 hover:text-blue-300">Reset filter</button>
        </div>
      )}

      {/* ── Measurement info ──────────────────────────────────────── */}
      {measureActive && hasResults && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 bg-yellow-900/90 rounded-lg px-4 py-2 backdrop-blur-md border border-yellow-700 text-[11px] text-yellow-200"
          style={{ zIndex: 20 }}>
          {!measurement?.point1 && 'Click a point to start measuring'}
          {measurement?.point1 && !measurement?.point2 && 'Click a second point to measure distance'}
          {measurement?.distance != null && (
            <span className="font-mono font-bold text-yellow-100">
              Distance: {measurement.distance.toFixed(3)} mm
            </span>
          )}
        </div>
      )}

      {/* ── Animation indicator ────────────────────────────────────── */}
      {animPlaying && hasResults && (
        <div className="absolute top-16 right-4 bg-purple-900/90 rounded-lg px-3 py-1.5 backdrop-blur-md border border-purple-700 text-[10px] text-purple-200 flex items-center gap-2"
          style={{ zIndex: 20 }}>
          <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
          Animating deformation
        </div>
      )}

      {/* ── Hover node info tooltip (above the floating action bar) ── */}
      {hoveredNode && hasResults && !measureActive && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-gray-900/95 rounded-lg px-4 py-2 backdrop-blur-md border border-gray-700 flex gap-4 text-[11px]"
          style={{ zIndex: 20 }}>
          <div>
            <span className="text-gray-500">Node </span>
            <span className="text-gray-300 font-mono">{hoveredNode.index}</span>
          </div>
          <div>
            <span className="text-gray-500">Stress </span>
            <span className="text-orange-400 font-mono font-bold">{hoveredNode.stress.toFixed(2)} MPa</span>
          </div>
          <div>
            <span className="text-gray-500">Disp </span>
            <span className="text-gray-300 font-mono">
              {Math.sqrt(hoveredNode.displacement[0]**2 + hoveredNode.displacement[1]**2 + hoveredNode.displacement[2]**2).toFixed(4)} mm
            </span>
          </div>
          <div>
            <span className="text-gray-500">Pos </span>
            <span className="text-gray-300 font-mono">
              ({hoveredNode.position[0].toFixed(1)}, {hoveredNode.position[1].toFixed(1)}, {hoveredNode.position[2].toFixed(1)})
            </span>
          </div>
        </div>
      )}

      {/* ── Deformation + color legend (results) ───────────────────── */}
      {hasResults && (
        <div className="absolute bottom-20 right-4 bg-gray-900/90 rounded-xl p-4 backdrop-blur-md border border-gray-800" style={{ zIndex: 20 }}>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Deformation</p>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[10px] text-gray-500">1x</span>
            <input type="range" min="0" max="100" step="1" value={deformationScale}
              onChange={e => setDeformationScale(parseFloat(e.target.value))} className="w-24 accent-blue-500" />
            <span className="text-[10px] text-gray-500">100x</span>
          </div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Von Mises Stress (MPa)</p>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-blue-400 font-mono">
              {results.results.min_von_mises_pa > 0 ? (results.results.min_von_mises_pa / 1e6).toFixed(1) : '0'}
            </span>
            <div className="w-28 h-3 rounded-full" style={{ background: COLORMAP_GRADIENTS[colormap] }} />
            <span className="text-[10px] text-red-400 font-mono">{maxMpa}</span>
          </div>
        </div>
      )}

      {/* ── Results info (top left) ────────────────────────────────── */}
      {hasResults && verdict && (
        <div className={`absolute top-4 left-4 bg-gray-900/90 rounded-xl p-4 backdrop-blur-md border border-gray-800 shadow-xl ${verdict.bgGlow}`} style={{ zIndex: 20 }}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`text-2xl font-black ${verdict.color}`}>{sf >= 9999 ? 'N/A' : sf.toFixed(1)}</div>
            <div>
              <p className={`text-sm font-bold ${verdict.color}`}>{verdict.label}</p>
              <p className="text-[10px] text-gray-500">Safety Factor</p>
            </div>
          </div>
          <div className="space-y-1.5 text-[11px]">
            <div className="flex justify-between gap-6">
              <span className="text-gray-500">Peak Stress</span>
              <span className="text-gray-200 font-mono">{maxMpa} MPa <span className="text-gray-600">{stressPlainText(maxMpa)}</span></span>
            </div>
            <div className="flex justify-between gap-6">
              <span className="text-gray-500">Max Flex</span>
              <span className="text-gray-200 font-mono">{maxDisp} mm <span className="text-gray-600">{dispPlainText(maxDisp)}</span></span>
            </div>
            <div className="flex justify-between gap-6">
              <span className="text-gray-500">Nodes</span>
              <span className="text-gray-200 font-mono">{nodeData.positions.length.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── STL Preview info ───────────────────────────────────────── */}
      {!hasResults && stlGeometry && stlStats && (
        <div className="absolute top-4 left-4 bg-gray-900/90 rounded-xl px-4 py-3 backdrop-blur-md border border-gray-800" style={{ zIndex: 20 }}>
          <p className="text-xs font-semibold text-gray-300 mb-2">STL Preview</p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
            <span className="text-gray-500">Triangles</span><span className="text-gray-300">{stlStats.triCount.toLocaleString()}</span>
            <span className="text-gray-500">Size X</span><span className="text-gray-300">{stlStats.sizeX} mm</span>
            <span className="text-gray-500">Size Y</span><span className="text-gray-300">{stlStats.sizeY} mm</span>
            <span className="text-gray-500">Size Z</span><span className="text-gray-300">{stlStats.sizeZ} mm</span>
          </div>
        </div>
      )}

      {/* ── Controls hint ──────────────────────────────────────────── */}
      {showViewer && !hasResults && (
        <div className="absolute bottom-4 left-4 bg-gray-900/70 rounded-lg px-3 py-1.5 backdrop-blur text-[10px] text-gray-500 flex gap-3" style={{ zIndex: 20 }}>
          <span>Rotate: Left drag</span>
          <span>Pan: Right drag</span>
          <span>Zoom: Scroll</span>
        </div>
      )}
    </div>
  )
}
